// src/components/ConversationList.tsx

import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { DMConversation } from '../types';

interface ConversationListProps {
  accountId?: string;
  onSelectConversation: (_conv: DMConversation) => void;
}

export function ConversationList({ accountId, onSelectConversation }: ConversationListProps) {
  const [conversations, setConversations] = useState<DMConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getConversations(accountId);
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleSelect = (conversation: DMConversation) => {
    setSelectedId(conversation.id);
    onSelectConversation(conversation);
  };

  if (loading) {
    return <div className="p-4">Loading conversations...</div>;
  }

  if (conversations.length === 0) {
    return <div className="p-4 text-gray-500">No conversations yet</div>;
  }

  return (
    <div className="border-r border-gray-200 h-full overflow-y-auto">
      {conversations.map((conv) => (
        <div
          key={conv.id}
          onClick={() => handleSelect(conv)}
          className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
            selectedId === conv.id ? 'bg-blue-50' : ''
          }`}
        >
          <div className="font-medium">{conv.platform_username || 'Unknown User'}</div>
          <div className="text-sm text-gray-500">
            {conv.message_count} messages
          </div>
          {conv.last_message_at && (
            <div className="text-xs text-gray-400 mt-1">
              {new Date(conv.last_message_at).toLocaleDateString()}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
